import { Flex, Form, Skeleton, Spin, Typography } from "antd"
import React, { useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { type RootState } from "../../../store.ts"
import {
  selectActiveBranch,
  selectActiveBranchId,
  selectCompanyIsMultiBranch
} from "../../Branches/redux/branches.slice.ts"
import { fetchTeamMembersFilterOptions } from "../../TeamManagementPage/redux/team-members.thunks.ts"
import {
  SettingsCompanyForm,
  type SettingsCompanyFormData
} from "../components/settings-company/SettingsCompanyForm.tsx"
import { fetchUser, updateUser } from "../redux/settings.thunks.ts"

const SettingsCompanyPage = ({
  userId,
  activeBranchId,
  activeBranchName,
  isMultiBranch,
  fullUser,
  fetching,
  saveFetching,
  options,
  companyPermissions,
  //
  getFilterOptions,
  getUser,
  updateUser
}: ConnectedProps<typeof connector>) => {
  const [form] = Form.useForm<SettingsCompanyFormData>()

  // A single-operation account's one Team is always the "active branch" by
  // id (`branches.slice.ts`'s first-load fallback), so `activeBranchId`
  // alone can never tell a real branch workspace apart from a
  // single-operation company — `isMultiBranch` gates it, same fix as
  // `SettingsLayoutTabs.tsx`'s identical `isBranchWorkspace`. `undefined`,
  // never `null` — an absent `team` on the request means "Company HQ" (the
  // caller's own default branch), same contract the backend's
  // `resolve_acting_team` already expects.
  const isBranchScope = isMultiBranch && !!activeBranchId
  const team = isBranchScope ? activeBranchId : undefined

  const onReset = () => form.resetFields()

  const handleFinish = async ({ logo, ...values }: SettingsCompanyFormData) => {
    try {
      await updateUser({
        id: userId,
        team,
        company_details: {
          // if not src
          ...(typeof logo !== "string" ? { logo } : {}),
          ...values
        }
      }).unwrap()
      form.resetFields()
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    generateDocumentTitle("Settings Company")
  }, [])

  useEffect(() => {
    if (userId) {
      // Re-fetches whenever the workspace selector moves to a different
      // branch (or back to Company HQ) — previously this effect depended
      // only on `userId`, so switching branches while already on this page
      // left whichever branch's data loaded first on screen indefinitely,
      // and every save silently wrote to that same stale branch too. The
      // slice's own `fetchUserRequestId` guard (see `settings.slice.ts`)
      // keeps an older in-flight fetch for a DIFFERENT branch from landing
      // after this one and repainting the page with the wrong data.
      getUser({ id: userId, team })
      getFilterOptions()
    }
  }, [userId, team])

  // Only a branch workspace gets this header: "Branch settings · <name>"
  // names something the tab bar's own "Branch & System" label doesn't —
  // which branch. In Company HQ/single-operation, the same content would
  // just restate the "Company & System" tab immediately above it, so no
  // heading renders there at all.
  const heading = isBranchScope ? `Branch settings · ${activeBranchName ?? ""}` : null
  const subtitle = "Manage settings for this branch."

  return (
    // ONE wrapping element, not a bare Fragment: `SettingsLayout`'s own
    // `<Flex flex={1}><Outlet /></Flex>` is a row flex (antd's default
    // direction), so two top-level siblings here would flatten straight
    // into that row and render side by side — a narrow heading column
    // beside the form — instead of stacked. `flex-col` here is what makes
    // the header genuinely full-width, above the form.
    <div className="flex flex-col gap-3 flex-1 w-full">
      {heading && (
        <div className="mb-0 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <Typography.Title level={5} className="!mb-0.5">
            {heading}
          </Typography.Title>
          <Typography.Text type="secondary">{subtitle}</Typography.Text>
        </div>
      )}
      {fetching ? (
        <Flex flex={1}>
          <Spin fullscreen size="large" />
          <Skeleton.Node className={"size-full"} />
        </Flex>
      ) : (
        <SettingsCompanyForm
          // Remounts the form on every branch switch, instead of merely
          // re-seeding `initialValues` on an antd `Form` instance that
          // stays mounted — antd only applies `initialValues` once, on
          // first mount, and otherwise leaves whatever the previous
          // branch's fields held (including any value the user had typed
          // but not saved) visibly on screen under the new branch's
          // heading until a manual edit happened to touch every field.
          key={team ?? "company-hq"}
          form={form}
          options={options}
          saving={saveFetching}
          readonly={!companyPermissions?.settings_company_edit}
          isBranchScope={isBranchScope}
          branchName={activeBranchName}
          onReset={onReset}
          onFinish={handleFinish}
          initialValues={fullUser?.company_details}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.settings.fetching || state.users.fetchingFilterOptions,
  saveFetching:
    state.settings.saveFetching || state.settings.savePasswordFetching,
  options: state.users.filterOptions,
  fullUser: state.settings.user,
  userId: state?.auth?.userInfo?.id,
  activeBranchId: selectActiveBranchId(state),
  activeBranchName: selectActiveBranch(state)?.name,
  isMultiBranch: selectCompanyIsMultiBranch(state),
  companyPermissions:
    state?.auth?.userInfo?.permissions?.codes?.settings?.company
})

const mapDispatchToProps = {
  getFilterOptions: fetchTeamMembersFilterOptions,
  getUser: fetchUser,
  updateUser: updateUser
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SettingsCompanyPage)
